import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Plus, Search, Edit, Trash2, Eye, Loader2, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import { productsAPI, categoriesAPI } from '../../services/api';

const AdminProducts = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedProducts, setSelectedProducts] = useState([]);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const productsPerPage = 50;

  const fetchProducts = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const skip = (page - 1) * productsPerPage;
      const response = await productsAPI.getAll({ limit: productsPerPage, skip });
      const productsData = Array.isArray(response.data) ? response.data : 
                          Array.isArray(response) ? response : [];
      
      setProducts(productsData);
      
      // Get total count
      const countRes = await productsAPI.getCount();
      setTotalProducts(countRes.data?.count || countRes.count || 0);
      
      console.log(`✅ Page ${page}: ${productsData.length} products loaded`);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const categoriesRes = await categoriesAPI.getAll();
        const categoriesData = Array.isArray(categoriesRes.data) ? categoriesRes.data : 
                              Array.isArray(categoriesRes) ? categoriesRes : [];
        setCategories(categoriesData);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    
    fetchCategories();
    fetchProducts(1);
  }, [fetchProducts]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    fetchProducts(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const totalPages = Math.ceil(totalProducts / productsPerPage);

  const categoryOptions = ['all', ...categories.map(c => c.name)];

  // Client-side filtering for current page
  const filteredProducts = products.filter(p => {
    const matchesSearch = p.name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || p.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleDelete = async (productId) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await productsAPI.delete(productId);
        setProducts(products.filter(p => (p._id || p.id) !== productId));
        alert('Product deleted successfully!');
      } catch (error) {
        console.error('Error deleting product:', error);
        alert('Failed to delete product: ' + (error.response?.data?.detail || error.message));
      }
    }
  };

  const handleBulkDelete = async () => {
    if (selectedProducts.length === 0) {
      alert('Select products to delete');
      return;
    }
    if (window.confirm(`Delete ${selectedProducts.length} selected products?`)) {
      try {
        await productsAPI.bulkDelete(selectedProducts);
        setProducts(products.filter(p => !selectedProducts.includes(p._id || p.id)));
        setSelectedProducts([]);
        alert('Products deleted!');
      } catch (error) {
        console.error('Error bulk deleting products:', error);
        alert('Failed to delete products: ' + (error.response?.data?.detail || error.message));
      }
    }
  };

  const toggleSelect = (productId) => {
    setSelectedProducts(prev =>
      prev.includes(productId) ? prev.filter(id => id !== productId) : [...prev, productId]
    );
  };

  const selectAll = () => {
    if (selectedProducts.length === filteredProducts.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(filteredProducts.map(p => p._id || p.id));
    }
  };

  const handleEdit = (productId) => {
    navigate(`/admin/products/edit/${productId}`);
  };

  const handleView = (product) => {
    window.open(`/shop/product/${product.slug}`, '_blank');
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-accent" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-charcoal font-heading">Products</h1>
            <p className="text-stone-grey font-body">{totalProducts} total products</p>
          </div>
          <div className="flex gap-3">
            <Button 
              onClick={fetchProducts}
              variant="outline" 
              className="border-accent text-accent hover:bg-accent hover:text-white font-body"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            {selectedProducts.length > 0 && (
              <Button 
                onClick={handleBulkDelete}
                variant="outline" 
                className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white font-body"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Selected ({selectedProducts.length})
              </Button>
            )}
            <Button className="bg-[#2C2824] hover:bg-[#1a1816] text-white font-body px-6 py-3 h-12 text-base shadow-md hover:shadow-lg transition-all" onClick={() => navigate('/admin/products/create')}>
              <Plus className="h-5 w-5 mr-2" />
              Add Product
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg p-6 border border-soft-sand shadow-soft">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-grey" />
              <Input
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 font-body"
              />
            </div>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-4 py-2 border border-soft-sand rounded bg-white text-charcoal font-body focus:border-muted-green focus:ring-muted-green"
            >
              {categoryOptions.map((cat) => (
                <option key={cat} value={cat}>
                  {cat === 'all' ? 'All Categories' : cat}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Products Table */}
        <div className="bg-white rounded-lg border border-soft-sand shadow-soft overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-soft-sand">
                <tr>
                  <th className="px-6 py-4 w-12">
                    <input
                      type="checkbox"
                      checked={selectedProducts.length === filteredProducts.length && filteredProducts.length > 0}
                      onChange={selectAll}
                      className="w-5 h-5"
                    />
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-charcoal font-body">Product</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-charcoal font-body">SKU</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-charcoal font-body">Category</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-charcoal font-body">Price</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-charcoal font-body">Stock</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-charcoal font-body">Status</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-charcoal font-body">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-soft-sand">
                {filteredProducts.map((product) => {
                  const productId = product._id || product.id;
                  const images = product.images || [];
                  const salePrice = product.sale_price || product.salePrice;
                  const inStock = product.in_stock !== undefined ? product.in_stock : product.inStock;
                  const isNew = product.is_new || product.isNew;
                  const isBestSeller = product.is_best_seller || product.isBestSeller;
                  
                  return (
                    <tr key={productId} className="hover:bg-soft-sand/30 transition-colors">
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={selectedProducts.includes(productId)}
                          onChange={() => toggleSelect(productId)}
                          className="w-5 h-5"
                        />
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <img
                            src={images[0] || '/placeholder.png'}
                            alt={product.name}
                            className="w-12 h-12 object-cover rounded bg-soft-sand"
                          />
                          <span className="font-medium text-charcoal font-body">{product.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-stone-grey font-body">{product.sku}</td>
                      <td className="px-6 py-4 text-sm text-stone-grey font-body">{product.category}</td>
                      <td className="px-6 py-4 text-sm font-medium text-charcoal font-body">
                        {salePrice ? (
                          <div>
                            <p className="text-muted-green">${salePrice.toFixed(2)}</p>
                            <p className="text-xs line-through text-stone-grey">${product.price.toFixed(2)}</p>
                          </div>
                        ) : (
                          `$${product.price.toFixed(2)}`
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-stone-grey font-body">
                        {inStock !== false ? (
                          <span className="text-muted-green">In Stock</span>
                        ) : (
                          <span className="text-soft-terracotta">Out of Stock</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {isNew && (
                            <span className="text-xs px-2 py-1 bg-soft-terracotta/10 text-soft-terracotta rounded font-body">New</span>
                          )}
                          {isBestSeller && (
                            <span className="text-xs px-2 py-1 bg-muted-green/10 text-muted-green rounded font-body">Best Seller</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <Button variant="ghost" size="icon" className="text-charcoal hover:bg-soft-sand" onClick={() => handleView(product)}>
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="text-charcoal hover:bg-soft-sand" onClick={() => handleEdit(productId)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="text-soft-terracotta hover:bg-soft-terracotta/10" onClick={() => handleDelete(productId)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          <div className="px-6 py-4 border-t border-soft-sand bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="text-sm text-[#6B6560] font-body">
                Showing {((currentPage - 1) * productsPerPage) + 1} - {Math.min(currentPage * productsPerPage, totalProducts)} of {totalProducts} products
              </div>
              
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="border-[#E8E4DE] text-[#2C2824]"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                
                <div className="flex items-center gap-1">
                  {[...Array(Math.min(5, totalPages))].map((_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <Button
                        key={pageNum}
                        variant={currentPage === pageNum ? "default" : "outline"}
                        size="sm"
                        onClick={() => handlePageChange(pageNum)}
                        className={currentPage === pageNum 
                          ? "bg-[#2C2824] text-white" 
                          : "border-[#E8E4DE] text-[#2C2824]"
                        }
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="border-[#E8E4DE] text-[#2C2824]"
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminProducts;